import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import Upload from "src/components/upload/upload";
import { useSnackbar } from "src/components/snackbar";
import { propuestasApi } from "src/services/propuestasApi";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import { derivarDecisionesPorItem } from "src/utils/propuestasV2/decisionesRenegociacionV2";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  alertBox: {
    marginBottom: theme.spacing(2),
  },
  itemRow: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  itemRowBloqueado: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  motivoField: {
    marginTop: theme.spacing(1),
  },
  commentField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const MAX_SIZE_FILE = 50 * 1024 * 1024; // 50 MB
const MAX_FILES = 1;

/**
 * Construye el estado inicial de decisiones para los ítems editables: todos en
 * ACEPTADO (caso más común: el cliente aceptó la propuesta completa por una vía
 * externa). Los ítems ya aceptados no entran acá: no se pueden reenviar.
 */
function buildDecisionesIniciales(itemsEditables) {
  const mapa = {};
  for (const item of itemsEditables) {
    mapa[item.id] = { decision: "ACEPTADO", comentario_cliente: "" };
  }
  return mapa;
}

/**
 * Obtiene el texto de display de un item: prioriza faseTitulo, luego nombreServicio.
 */
function getNombreItem(item) {
  return item.faseTitulo || item.nombreServicio || `Item #${item.id}`;
}

/**
 * Registro por el canal interno de la aceptación o el rechazo que el cliente
 * comunicó por una vía externa, con su comprobante. Un servicio que el cliente
 * ya aceptó queda fuera de lo que se envía: ya se materializó como plan o tarea
 * y no vuelve a discutirse.
 */
export default function ModalAceptacionInternaV2({
  open,
  setOpen,
  propuesta,
  onSuccess,
  maxWidth = "md",
  fullWidth = true,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [aceptarInternaV2] = propuestasApi.useAceptarInternaV2Mutation();

  const items = useMemo(
    () => (Array.isArray(propuesta?.items) ? propuesta.items : []),
    [propuesta],
  );

  // La decisión vigente de cada ítem viaja en `item.respuesta` — no se deriva
  // del hilo, que conserva en su bitácora decisiones que la ronda siguiente ya
  // borró de la tabla de respuestas.
  const decisionesPorItem = useMemo(
    () => derivarDecisionesPorItem(items),
    [items],
  );

  // Un servicio aceptado ya se materializó como plan o tarea y no vuelve a la
  // mesa. Separarlo acá es lo que evita mandarlo en el cuerpo: el backend lo
  // rechaza recién bajo el lock, después de subir el comprobante, y el archivo
  // queda huérfano en el almacenamiento.
  const itemsBloqueados = useMemo(
    () => items.filter((item) => decisionesPorItem.get(item.id) === "ACEPTADO"),
    [items, decisionesPorItem],
  );
  const itemsEditables = useMemo(
    () => items.filter((item) => decisionesPorItem.get(item.id) !== "ACEPTADO"),
    [items, decisionesPorItem],
  );

  const [file, setFile] = useState(null);
  const [comentarios, setComentarios] = useState("");
  const [decisiones, setDecisiones] = useState(() =>
    buildDecisionesIniciales(itemsEditables),
  );
  const [procesando, setProcesando] = useState(false);

  // Reiniciar estado al abrir/cerrar el modal.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setComentarios("");
      setDecisiones({});
      setProcesando(false);
    }
    // Solo reaccionar al cambio de open; itemsEditables se reconcilia abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // itemsEditables cambia de referencia en cada render de propuesta.items; la
  // reconciliación de abajo solo necesita reaccionar cuando cambia el conjunto
  // de ids, no la identidad del arreglo.
  const idsEditables = itemsEditables.map((item) => item.id).join(",");

  // Reconciliar las decisiones si propuesta.items cambia mientras el modal está
  // abierto (por ejemplo, un refetch en segundo plano). Conserva lo ya elegido
  // cuando el ítem sigue siendo editable.
  useEffect(() => {
    if (!open) return;
    setDecisiones((prev) => {
      const nuevo = buildDecisionesIniciales(itemsEditables);
      for (const id of Object.keys(nuevo)) {
        if (prev[id]) nuevo[id] = prev[id];
      }
      return nuevo;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsEditables]);

  const handleClose = () => {
    if (!procesando) {
      setOpen(false);
    }
  };

  const handleDropFile = useCallback((acceptedFiles) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      setFile(
        Object.assign(newFile, {
          preview: URL.createObjectURL(newFile),
        }),
      );
    } else {
      setFile(null);
    }
  }, []);

  const handleDecisionChange = (itemId, nuevaDecision) => {
    setDecisiones((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        decision: nuevaDecision,
        // Limpiar el comentario si se cambia a ACEPTADO
        comentario_cliente:
          nuevaDecision === "ACEPTADO"
            ? ""
            : (prev[itemId]?.comentario_cliente ?? ""),
      },
    }));
  };

  const handleComentarioChange = (itemId, valor) => {
    setDecisiones((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], comentario_cliente: valor },
    }));
  };

  const validar = () => {
    if (!file) {
      enqueueSnackbar(
        "Debes adjuntar el comprobante de la aceptación del cliente.",
        {
          variant: "error",
        },
      );
      return false;
    }
    if (itemsEditables.length === 0) {
      enqueueSnackbar("No quedan servicios pendientes de respuesta.", {
        variant: "error",
      });
      return false;
    }
    // La decisión se exige solo sobre los servicios que siguen en la mesa:
    // pedirla también sobre los ya aceptados dejaría el envío bloqueado para
    // siempre, porque esos nunca reciben una decisión nueva.
    for (const item of itemsEditables) {
      const d = decisiones[item.id];
      if (!d || !d.decision) {
        enqueueSnackbar(
          `Debes elegir una decisión para: ${getNombreItem(item)}`,
          {
            variant: "error",
          },
        );
        return false;
      }
    }
    return true;
  };

  const handleConfirmar = async () => {
    if (!validar()) return;

    // El cuerpo cubre solo los servicios que siguen en la mesa: reenviar uno ya
    // aceptado lo rechaza el backend después de subir el comprobante, y el
    // archivo queda huérfano en el almacenamiento.
    const respuestas = itemsEditables.map((item) => {
      const d = decisiones[item.id];
      const entrada = { item_id: item.id, decision: d.decision };
      if (d.decision === "RECHAZADO" && d.comentario_cliente?.trim()) {
        entrada.comentario_cliente = d.comentario_cliente.trim();
      }
      return entrada;
    });

    const formData = new FormData();
    formData.append("respuestas", JSON.stringify(respuestas));
    if (comentarios.trim()) {
      formData.append("comentarios", comentarios.trim());
    }
    formData.append("comprobante", file);

    setProcesando(true);
    try {
      await aceptarInternaV2({ id: propuesta.id, formData }).unwrap();
      enqueueSnackbar("Propuesta aceptada internamente con éxito.", {
        variant: "success",
      });
      handleClose();
      onSuccess?.();
    } catch (error) {
      if (error?.data?.serviciosActivos?.length) {
        await AlertaModalV2({
          icon: "error",
          title: "No se pudo aceptar la propuesta",
          text: error.data.message,
          confirmButtonText: "Entendido",
          showCancelButton: false,
        }).fire();
      } else {
        enqueueSnackbar(
          error?.data?.message ||
            error?.message ||
            "Ocurrió un error al procesar la aceptación interna.",
          { variant: "error" },
        );
      }
    } finally {
      setProcesando(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={maxWidth}
      onClose={handleClose}
      fullWidth={fullWidth}
    >
      <DialogTitle className={classes.dialogTitle}>
        Aceptación interna — Propuesta #{propuesta?.id}
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={procesando}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" className={classes.alertBox}>
          <Typography variant="body2">
            <strong>Aceptación interna:</strong> Usa esta opción cuando el
            cliente haya aceptado la propuesta por una vía externa (correo,
            WhatsApp, llamada telefónica, etc.). Debes adjuntar un comprobante
            que respalde la aceptación, como una captura de pantalla o un
            documento firmado.
          </Typography>
        </Alert>

        {/* Servicios cerrados: se listan para que quede claro por qué no se
            pueden tocar, y quedan fuera de lo que se envía. */}
        {itemsBloqueados.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Ya aceptados (no editables)
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              component="p"
              sx={{ mb: 1 }}
            >
              El cliente ya los aceptó y su plan o tarea se creó, así que no
              vuelven a la mesa ni cambian de decisión.
            </Typography>
            <Stack spacing={1}>
              {itemsBloqueados.map((item) => (
                <Box key={item.id} className={classes.itemRowBloqueado}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {getNombreItem(item)}
                    </Typography>
                    <Chip
                      label="Aceptado"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Lista de items con control de decision */}
        {itemsEditables.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Decisión por ítem
            </Typography>
            <Stack spacing={1.5}>
              {itemsEditables.map((item) => {
                const d = decisiones[item.id] ?? {
                  decision: "ACEPTADO",
                  comentario_cliente: "",
                };
                const esRechazado = d.decision === "RECHAZADO";
                return (
                  <Box key={item.id} className={classes.itemRow}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getNombreItem(item)}
                        {item.tipoItem && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="textSecondary"
                            sx={{ ml: 1 }}
                          >
                            ({item.tipoItem})
                          </Typography>
                        )}
                      </Typography>
                      <RadioGroup
                        row
                        value={d.decision}
                        onChange={(e) =>
                          handleDecisionChange(item.id, e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="ACEPTADO"
                          control={<Radio size="small" color="success" />}
                          label={
                            <Typography variant="body2">Aceptado</Typography>
                          }
                          disabled={procesando}
                        />
                        <FormControlLabel
                          value="RECHAZADO"
                          control={<Radio size="small" color="error" />}
                          label={
                            <Typography variant="body2">Rechazado</Typography>
                          }
                          disabled={procesando}
                        />
                      </RadioGroup>
                    </Stack>
                    {esRechazado && (
                      <TextField
                        className={classes.motivoField}
                        fullWidth
                        size="small"
                        label="Motivo del rechazo (opcional)"
                        placeholder="Indica el motivo por el que el cliente rechazó este ítem..."
                        value={d.comentario_cliente}
                        onChange={(e) =>
                          handleComentarioChange(item.id, e.target.value)
                        }
                        disabled={procesando}
                        variant="outlined"
                        inputProps={{ maxLength: 500 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ) : (
          // Sin servicios en la mesa no hay nada válido que enviar: el backend
          // exige al menos uno y responde con un conflicto si no queda ninguno.
          <Alert
            severity={items.length === 0 ? "warning" : "info"}
            sx={{ mb: 2 }}
          >
            {items.length === 0
              ? "Esta propuesta no tiene ítems registrados. Verifica que la propuesta esté completa antes de registrar la aceptación."
              : "El cliente ya aceptó todos los servicios de esta propuesta: no queda ninguno por registrar."}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Comprobante de respaldo */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Comprobante de respaldo *
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Adjunta una imagen o PDF que respalde la aceptación del cliente
            (captura de correo, WhatsApp, documento firmado, etc.).
          </Typography>
          <Upload
            disabled={procesando}
            file={file}
            loading={false}
            onDrop={handleDropFile}
            onDelete={() => setFile(null)}
            accept={{
              "application/pdf": [".pdf"],
              "image/*": [".png", ".jpg", ".jpeg"],
            }}
            maxSize={MAX_SIZE_FILE}
            maxFiles={MAX_FILES}
          />
        </Box>

        {/* Comentarios adicionales */}
        <TextField
          className={classes.commentField}
          fullWidth
          multiline
          rows={3}
          label="Comentarios adicionales (opcional)"
          placeholder="Ej: El cliente confirmó vía WhatsApp el día 15/01/2025..."
          value={comentarios}
          onChange={(e) => setComentarios(e.target.value)}
          disabled={procesando}
          variant="outlined"
          inputProps={{ maxLength: 500 }}
          helperText={`${comentarios.length}/500 caracteres`}
        />
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={handleClose} disabled={procesando}>
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={procesando}
          disabled={procesando || itemsEditables.length === 0}
          onClick={handleConfirmar}
        >
          Registrar aceptación
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

ModalAceptacionInternaV2.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  propuesta: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  maxWidth: PropTypes.string,
  fullWidth: PropTypes.bool,
};

ModalAceptacionInternaV2.defaultProps = {
  onSuccess: undefined,
  maxWidth: "md",
  fullWidth: true,
};
